/* In-memory mocked client for Documents — no real backend exists for this domain yet. */

import {
  DocumentFolderDto,
  DocumentDto,
  DocumentVersionDto,
  FolderPermissionDto,
  FolderTreeNodeDto,
  DocumentSearchParams,
  DocumentSearchResult,
  CreateFolderRequest,
  UpdateFolderRequest,
  UploadDocumentRequest,
  UpdateDocumentRequest,
  UploadVersionRequest,
  SetFolderPermissionRequest,
  StoreTaskEvidenceRequest,
  DocumentScope,
  DocumentCategory,
  DocumentPermission,
  DocumentType,
} from './documentsTypes';
import { IDocumentFoldersClient, IDocumentsClient } from './IDocumentsClient';

type MockFolderRecord = {
  id: number;
  name: string;
  description?: string | null;
  parentFolderId: number | null;
  scope: DocumentScope;
  category: DocumentCategory;
  franchiseId?: number | null;
  storeId?: number | null;
  taskTemplateId?: number | null;
  taskInstanceId?: number | null;
  evidenceDate?: string | null;
  createdAt: string;
  createdByUserId: number;
  updatedAt?: string | null;
};

const defaultPermissions: DocumentPermission[] = [
  DocumentPermission.View,
  DocumentPermission.Upload,
  DocumentPermission.Download,
  DocumentPermission.Edit,
  DocumentPermission.Delete,
  DocumentPermission.ManagePermissions,
  DocumentPermission.CreateFolder,
];

const mockFolders: MockFolderRecord[] = [
  {
    id: 1,
    name: 'Policies',
    description: 'Franchise policy documents',
    parentFolderId: null,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Policies,
    franchiseId: 10,
    createdAt: '2024-01-10T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 2,
    name: 'Training Materials',
    description: 'Training and onboarding guides',
    parentFolderId: null,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Training,
    franchiseId: 10,
    createdAt: '2024-01-15T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 3,
    name: 'Health & Safety',
    description: 'H&S and compliance subfolder',
    parentFolderId: 1,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Compliance,
    franchiseId: 10,
    createdAt: '2024-02-01T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 4,
    name: 'SOPs',
    description: 'Operating procedures',
    parentFolderId: 1,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Operations,
    franchiseId: 10,
    createdAt: '2024-02-10T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 5,
    name: 'Kitchen SOPs',
    description: 'Kitchen-level procedures',
    parentFolderId: 4,
    scope: DocumentScope.Store,
    category: DocumentCategory.Operations,
    franchiseId: 10,
    storeId: 1001,
    createdAt: '2024-02-20T10:00:00Z',
    createdByUserId: 1,
  },
];

const mockFolderPermissions: FolderPermissionDto[] = [
  {
    id: 100,
    folderId: 1,
    roleId: 1,
    userId: null,
    franchiseId: 10,
    storeId: null,
    permissions: defaultPermissions,
    createdAt: '2024-01-10T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 101,
    folderId: 5,
    roleId: 2,
    userId: null,
    franchiseId: 10,
    storeId: 1001,
    permissions: [DocumentPermission.View, DocumentPermission.Upload, DocumentPermission.Download],
    createdAt: '2024-02-20T10:00:00Z',
    createdByUserId: 1,
  },
];

const makeVersion = (
  id: number,
  documentId: number,
  versionNumber: number,
  fileName: string,
  fileSize: number,
  documentType: DocumentType,
  createdAt: string,
  createdByUserName: string,
): DocumentVersionDto => ({
  id,
  documentId,
  versionNumber,
  fileName,
  fileSize,
  mimeType: 'application/octet-stream',
  documentType,
  blobUrl: `/mock/documents/${documentId}/v${versionNumber}`,
  thumbnailUrl: null,
  changeNotes: versionNumber > 1 ? `Version ${versionNumber} update` : null,
  createdAt,
  createdByUserId: 1,
  createdByUserName,
});

const mockDocuments: DocumentDto[] = [
  {
    id: 1,
    folderId: 1,
    name: 'Employee Handbook',
    description: 'Latest employee handbook',
    tags: ['hr', 'policy'],
    linkedTaskTemplateIds: [1],
    versionCount: 2,
    latestVersion: makeVersion(1001, 1, 2, 'employee-handbook-v2.pdf', 2048000, DocumentType.PDF, '2024-03-01T10:00:00Z', 'Admin'),
    versions: [
      makeVersion(1000, 1, 1, 'employee-handbook-v1.pdf', 1900000, DocumentType.PDF, '2024-02-01T10:00:00Z', 'Admin'),
      makeVersion(1001, 1, 2, 'employee-handbook-v2.pdf', 2048000, DocumentType.PDF, '2024-03-01T10:00:00Z', 'Admin'),
    ],
    createdAt: '2024-02-01T10:00:00Z',
    createdByUserId: 1,
    createdByUserName: 'Admin',
    updatedAt: '2024-03-01T10:00:00Z',
    updatedByUserId: 1,
  },
  {
    id: 2,
    folderId: 3,
    name: 'Food Safety Policy',
    description: 'Food safety and hygiene requirements',
    tags: ['compliance', 'haccp'],
    linkedTaskTemplateIds: [2],
    versionCount: 1,
    latestVersion: makeVersion(2000, 2, 1, 'food-safety-policy.pdf', 1024000, DocumentType.PDF, '2024-03-05T10:00:00Z', 'Admin'),
    versions: [
      makeVersion(2000, 2, 1, 'food-safety-policy.pdf', 1024000, DocumentType.PDF, '2024-03-05T10:00:00Z', 'Admin'),
    ],
    createdAt: '2024-03-05T10:00:00Z',
    createdByUserId: 1,
    createdByUserName: 'Admin',
  },
  {
    id: 3,
    folderId: 2,
    name: 'Barista Training Guide',
    description: 'Core barista training material',
    tags: ['training', 'coffee'],
    linkedTaskTemplateIds: [3],
    versionCount: 1,
    latestVersion: makeVersion(3000, 3, 1, 'barista-training-guide.docx', 5120000, DocumentType.Document, '2024-03-10T10:00:00Z', 'Training Team'),
    versions: [
      makeVersion(3000, 3, 1, 'barista-training-guide.docx', 5120000, DocumentType.Document, '2024-03-10T10:00:00Z', 'Training Team'),
    ],
    createdAt: '2024-03-10T10:00:00Z',
    createdByUserId: 1,
    createdByUserName: 'Training Team',
  },
  {
    id: 4,
    folderId: 5,
    name: 'Opening Procedure SOP',
    description: 'Store opening procedure',
    tags: ['operations', 'opening'],
    linkedTaskTemplateIds: [4],
    versionCount: 1,
    latestVersion: makeVersion(4000, 4, 1, 'opening-procedure-sop.pdf', 512000, DocumentType.PDF, '2024-03-15T10:00:00Z', 'Ops Manager'),
    versions: [
      makeVersion(4000, 4, 1, 'opening-procedure-sop.pdf', 512000, DocumentType.PDF, '2024-03-15T10:00:00Z', 'Ops Manager'),
    ],
    createdAt: '2024-03-15T10:00:00Z',
    createdByUserId: 1,
    createdByUserName: 'Ops Manager',
  },
];

const getFolderPath = (folderId: number): string[] => {
  const chain: string[] = [];
  let current = mockFolders.find((f) => f.id === folderId) || null;
  while (current) {
    chain.unshift(current.name);
    current = current.parentFolderId != null ? mockFolders.find((f) => f.id === current!.parentFolderId) || null : null;
  }
  return chain;
};

const toFolderDto = (folder: MockFolderRecord): DocumentFolderDto => {
  const childFolderCount = mockFolders.filter((f) => f.parentFolderId === folder.id).length;
  const documentCount = mockDocuments.filter((d) => d.folderId === folder.id).length;
  return {
    id: folder.id,
    name: folder.name,
    description: folder.description ?? null,
    parentFolderId: folder.parentFolderId,
    scope: folder.scope,
    category: folder.category,
    franchiseId: folder.franchiseId ?? null,
    storeId: folder.storeId ?? null,
    taskTemplateId: folder.taskTemplateId ?? null,
    taskInstanceId: folder.taskInstanceId ?? null,
    evidenceDate: folder.evidenceDate ?? null,
    path: getFolderPath(folder.id),
    childFolderCount,
    documentCount,
    effectivePermissions: defaultPermissions,
    createdAt: folder.createdAt,
    createdByUserId: folder.createdByUserId,
    updatedAt: folder.updatedAt ?? null,
  };
};

const buildTree = (parentFolderId: number | null): FolderTreeNodeDto[] => {
  return mockFolders
    .filter((f) => f.parentFolderId === parentFolderId)
    .map((f) => ({
      folder: toFolderDto(f),
      children: buildTree(f.id),
    }));
};

const getAncestorsInternal = (folderId: number): DocumentFolderDto[] => {
  const ancestors: DocumentFolderDto[] = [];
  let current = mockFolders.find((f) => f.id === folderId) || null;
  while (current && current.parentFolderId != null) {
    const parent = mockFolders.find((f) => f.id === current!.parentFolderId) || null;
    if (!parent) break;
    ancestors.unshift(toFolderDto(parent));
    current = parent;
  }
  return ancestors;
};

const nextId = (collection: { id: number }[]): number => {
  return collection.length ? Math.max(...collection.map((c) => c.id)) + 1 : 1;
};

export class DocumentFoldersClientMocked implements IDocumentFoldersClient {
  async getRootFolders(scope?: DocumentScope, _franchiseId?: number, _storeId?: number): Promise<DocumentFolderDto[]> {
    // Demo data ignores franchise/store scoping entirely - return all root folders regardless.
    let roots = mockFolders.filter((f) => f.parentFolderId === null);
    if (scope) roots = roots.filter((f) => f.scope === scope);
    return roots.map(toFolderDto);
  }

  async getFolder(id: number): Promise<DocumentFolderDto> {
    const folder = mockFolders.find((f) => f.id === id);
    if (!folder) throw new Error(`Folder ${id} not found`);
    return toFolderDto(folder);
  }

  async getAncestors(folderId: number): Promise<DocumentFolderDto[]> {
    const folder = mockFolders.find((f) => f.id === folderId);
    if (!folder) throw new Error(`Folder ${folderId} not found`);
    return getAncestorsInternal(folderId);
  }

  async searchFolders(query: string, _franchiseId?: number): Promise<DocumentFolderDto[]> {
    // Demo data ignores franchise scoping entirely - search across all folders regardless.
    let folders = [...mockFolders];
    const q = (query || '').toLowerCase();
    if (q) {
      folders = folders.filter((f) => f.name.toLowerCase().includes(q) || (f.description || '').toLowerCase().includes(q));
    }
    return folders.map(toFolderDto);
  }

  async getChildFolders(parentFolderId: number): Promise<DocumentFolderDto[]> {
    const folder = mockFolders.find((f) => f.id === parentFolderId);
    if (!folder) throw new Error(`Folder ${parentFolderId} not found`);
    return mockFolders.filter((f) => f.parentFolderId === parentFolderId).map(toFolderDto);
  }

  async getFolderTree(rootFolderId?: number): Promise<FolderTreeNodeDto[]> {
    if (rootFolderId != null) {
      const root = mockFolders.find((f) => f.id === rootFolderId);
      if (!root) return [];
      return [{ folder: toFolderDto(root), children: buildTree(root.id) }];
    }
    return buildTree(null);
  }

  async createFolder(request: CreateFolderRequest): Promise<DocumentFolderDto> {
    const newFolder: MockFolderRecord = {
      id: nextId(mockFolders),
      name: request.name,
      description: request.description ?? null,
      parentFolderId: request.parentFolderId ?? null,
      scope: request.scope,
      category: request.category,
      franchiseId: request.franchiseId ?? null,
      storeId: request.storeId ?? null,
      taskTemplateId: request.taskTemplateId ?? null,
      createdAt: new Date().toISOString(),
      createdByUserId: 1,
    };
    mockFolders.push(newFolder);
    return toFolderDto(newFolder);
  }

  async updateFolder(id: number, request: UpdateFolderRequest): Promise<DocumentFolderDto> {
    const folder = mockFolders.find((f) => f.id === id);
    if (!folder) throw new Error(`Folder ${id} not found`);
    folder.name = request.name ?? folder.name;
    folder.description = request.description ?? folder.description ?? null;
    folder.category = request.category ?? folder.category;
    folder.updatedAt = new Date().toISOString();
    return toFolderDto(folder);
  }

  async deleteFolder(id: number): Promise<void> {
    const hasChildren = mockFolders.some((f) => f.parentFolderId === id);
    const hasDocuments = mockDocuments.some((d) => d.folderId === id);
    if (hasChildren || hasDocuments) throw new Error('Folder is not empty');
    const index = mockFolders.findIndex((f) => f.id === id);
    if (index === -1) throw new Error(`Folder ${id} not found`);
    mockFolders.splice(index, 1);
  }

  async getFolderPermissions(folderId: number): Promise<FolderPermissionDto[]> {
    return mockFolderPermissions.filter((p) => p.folderId === folderId);
  }

  async setFolderPermission(folderId: number, request: SetFolderPermissionRequest): Promise<FolderPermissionDto> {
    const created: FolderPermissionDto = {
      id: nextId(mockFolderPermissions),
      folderId,
      roleId: request.roleId ?? null,
      userId: request.userId ?? null,
      franchiseId: request.franchiseId ?? null,
      storeId: request.storeId ?? null,
      permissions: request.permissions || [DocumentPermission.View],
      createdAt: new Date().toISOString(),
      createdByUserId: 1,
    };
    mockFolderPermissions.push(created);
    return created;
  }

  async removeFolderPermission(permissionId: number): Promise<void> {
    const index = mockFolderPermissions.findIndex((p) => p.id === permissionId);
    if (index > -1) mockFolderPermissions.splice(index, 1);
  }

  async getOrCreateTaskEvidenceFolder(
    taskTemplateId: number,
    taskTemplateName: string,
    storeId: number,
    evidenceDate: string,
  ): Promise<DocumentFolderDto> {
    let existing = mockFolders.find(
      (f) =>
        f.scope === DocumentScope.TaskEvidence &&
        f.storeId === storeId &&
        f.taskTemplateId === taskTemplateId &&
        f.evidenceDate === evidenceDate,
    );

    if (!existing) {
      const parentEvidenceFolder: MockFolderRecord = {
        id: nextId(mockFolders),
        name: `Store ${storeId} Evidence`,
        parentFolderId: null,
        scope: DocumentScope.TaskEvidence,
        category: DocumentCategory.Evidence,
        storeId,
        createdAt: new Date().toISOString(),
        createdByUserId: 1,
      };
      mockFolders.push(parentEvidenceFolder);

      existing = {
        id: nextId(mockFolders),
        name: `${taskTemplateName} - ${evidenceDate}`,
        parentFolderId: parentEvidenceFolder.id,
        scope: DocumentScope.TaskEvidence,
        category: DocumentCategory.Evidence,
        storeId,
        taskTemplateId,
        evidenceDate,
        createdAt: new Date().toISOString(),
        createdByUserId: 1,
      };
      mockFolders.push(existing);
    }

    return toFolderDto(existing);
  }
}

export class DocumentsClientMocked implements IDocumentsClient {
  async getDocuments(folderId: number): Promise<DocumentDto[]> {
    return mockDocuments.filter((d) => d.folderId === folderId);
  }

  async getDocument(id: number): Promise<DocumentDto> {
    const doc = mockDocuments.find((d) => d.id === id);
    if (!doc) throw new Error(`Document ${id} not found`);
    return doc;
  }

  async getDocumentWithVersions(id: number): Promise<DocumentDto> {
    const doc = mockDocuments.find((d) => d.id === id);
    if (!doc) throw new Error(`Document ${id} not found`);
    return { ...doc, versions: doc.versions || [doc.latestVersion] };
  }

  async getDocumentVersion(documentId: number, versionNumber: number): Promise<DocumentVersionDto> {
    const doc = mockDocuments.find((d) => d.id === documentId);
    if (!doc) throw new Error(`Document ${documentId} not found`);
    const version = (doc.versions || []).find((v) => v.versionNumber === versionNumber);
    if (!version) throw new Error(`Version ${versionNumber} not found for document ${documentId}`);
    return version;
  }

  async searchDocuments(params: DocumentSearchParams): Promise<DocumentSearchResult> {
    let docs = [...mockDocuments];
    if (params.folderId != null) docs = docs.filter((d) => d.folderId === params.folderId);
    const q = (params.query || '').toLowerCase();
    if (q) {
      docs = docs.filter(
        (d) =>
          d.name.toLowerCase().includes(q) ||
          (d.description || '').toLowerCase().includes(q) ||
          d.tags.some((t) => t.toLowerCase().includes(q)),
      );
    }
    const page = params.page || 1;
    const pageSize = params.pageSize || 50;
    const start = (Math.max(page, 1) - 1) * Math.max(pageSize, 1);
    const paged = docs.slice(start, start + pageSize);
    return { documents: paged, totalCount: docs.length, page, pageSize };
  }

  async getDocumentsForTaskTemplate(taskTemplateId: number): Promise<DocumentDto[]> {
    return mockDocuments.filter((d) => (d.linkedTaskTemplateIds || []).includes(taskTemplateId));
  }

  async uploadDocument(request: UploadDocumentRequest, file: File): Promise<DocumentDto> {
    const newId = nextId(mockDocuments);
    const version = makeVersion(newId + 9000, newId, 1, file?.name || `uploaded-document-${newId}.pdf`, file?.size ?? 1024, DocumentType.PDF, new Date().toISOString(), 'User');
    const doc: DocumentDto = {
      id: newId,
      folderId: request.folderId,
      name: request.name,
      description: request.description ?? null,
      tags: request.tags ?? [],
      linkedTaskTemplateIds: request.linkedTaskTemplateIds ?? [],
      versionCount: 1,
      latestVersion: version,
      versions: [version],
      createdAt: new Date().toISOString(),
      createdByUserId: 1,
      createdByUserName: 'User',
    };
    mockDocuments.push(doc);
    return doc;
  }

  async uploadVersion(request: UploadVersionRequest, file: File): Promise<DocumentDto> {
    const doc = mockDocuments.find((d) => d.id === request.documentId);
    if (!doc) throw new Error(`Document ${request.documentId} not found`);

    const newVersionNumber = doc.versionCount + 1;
    const newVersion = makeVersion(
      nextId((doc.versions || []).map((v) => ({ id: v.id }))),
      doc.id,
      newVersionNumber,
      file?.name || `${doc.name.replace(/\s+/g, '-').toLowerCase()}-v${newVersionNumber}.pdf`,
      file?.size ?? doc.latestVersion.fileSize,
      doc.latestVersion.documentType,
      new Date().toISOString(),
      'User',
    );
    newVersion.changeNotes = request.changeNotes ?? newVersion.changeNotes;

    doc.versionCount = newVersionNumber;
    doc.latestVersion = newVersion;
    doc.versions = [...(doc.versions || []), newVersion];
    doc.updatedAt = new Date().toISOString();
    doc.updatedByUserId = 1;
    return doc;
  }

  async updateDocument(id: number, request: UpdateDocumentRequest): Promise<DocumentDto> {
    const doc = mockDocuments.find((d) => d.id === id);
    if (!doc) throw new Error(`Document ${id} not found`);
    doc.name = request.name ?? doc.name;
    doc.description = request.description ?? doc.description ?? null;
    doc.tags = request.tags ?? doc.tags;
    doc.linkedTaskTemplateIds = request.linkedTaskTemplateIds ?? doc.linkedTaskTemplateIds;
    doc.updatedAt = new Date().toISOString();
    doc.updatedByUserId = 1;
    return doc;
  }

  async deleteDocument(id: number): Promise<void> {
    const index = mockDocuments.findIndex((d) => d.id === id);
    if (index === -1) throw new Error(`Document ${id} not found`);
    mockDocuments.splice(index, 1);
  }

  async storeTaskEvidence(request: StoreTaskEvidenceRequest, file: File, taskTemplateName: string): Promise<DocumentDto> {
    const newId = nextId(mockDocuments);
    const version = makeVersion(newId + 10000, newId, 1, file?.name || `task-evidence-${newId}.jpg`, file?.size ?? 256000, DocumentType.Image, new Date().toISOString(), 'User');
    const doc: DocumentDto = {
      id: newId,
      folderId: 1,
      name: `${taskTemplateName} Evidence ${newId}`,
      description: `Task evidence for ${request.evidenceType}`,
      tags: ['evidence'],
      linkedTaskTemplateIds: [request.taskTemplateId],
      versionCount: 1,
      latestVersion: version,
      versions: [version],
      createdAt: new Date().toISOString(),
      createdByUserId: 1,
      createdByUserName: 'User',
    };
    mockDocuments.push(doc);
    return doc;
  }
}
